// Pure formatting helpers for the Readout page (unit-tested in isolation).

import type { LedgerEntry, LiveMetric, MoneySummary } from '../../services/readout';

/** The API stores naive UTC; a stamp without an offset is UTC, not local. */
export function parseUtc(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const d = new Date(hasOffset ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatStamp(iso: string | null | undefined, withTime = false): string {
  const d = parseUtc(iso);
  if (!d) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  });
}

export function formatAge(days: number | null | undefined): string {
  if (days == null) return 'never';
  if (days < 1) return 'today';
  if (days < 2) return 'yesterday';
  if (days < 14) return `${Math.round(days)}d ago`;
  if (days < 60) return `${Math.round(days / 7)}w ago`;
  return `${Math.round(days / 30)}mo ago`;
}

export function formatUsd(value: number | null | undefined, { compact = false } = {}): string {
  if (value == null || Number.isNaN(value)) return '—';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (compact && abs >= 1000) {
    const k = abs / 1000;
    return `${sign}$${k >= 100 ? Math.round(k) : k.toFixed(1)}k`;
  }
  return `${sign}$${abs.toLocaleString('en-US', {
    minimumFractionDigits: abs < 100 ? 2 : 0,
    maximumFractionDigits: abs < 100 ? 2 : 0,
  })}`;
}

/** Render a live/agent metric by its unit. */
export function formatMetric(m: Pick<LiveMetric, 'value' | 'text_value' | 'unit'>): string {
  if (m.unit === 'text') return m.text_value ?? '—';
  if (m.value == null) return '—';
  switch (m.unit) {
    case 'usd':
      return formatUsd(m.value);
    case 'pct':
      return `${m.value.toFixed(m.value >= 10 ? 0 : 1)}%`;
    case 'ratio':
      return m.value.toFixed(2);
    case 'hours':
      return `${m.value.toLocaleString('en-US', { maximumFractionDigits: 1 })} h`;
    case 'minutes':
      return `${m.value.toLocaleString('en-US', { maximumFractionDigits: 0 })} min`;
    case 'days':
      return `${m.value.toLocaleString('en-US', { maximumFractionDigits: 1 })} d`;
    default:
      return m.value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }
}

export const KIND_LABELS: Record<string, string> = {
  cost: 'Cost',
  funding: 'Funding',
  benefit: 'Benefit',
  risk: 'Risk',
};

export const CATEGORY_LABELS: Record<string, string> = {
  ai_spend: 'AI spend',
  build_hours: 'Build hours',
  infra: 'Infrastructure',
  licenses: 'Licenses',
  client_engagement: 'Client engagement',
  internal_budget: 'Internal budget',
  hours_saved: 'Hours saved',
  revenue_protected: 'Revenue protected',
  revenue_won: 'Revenue won',
  cost_avoided: 'Cost avoided',
  quality: 'Quality',
  exposure: 'Exposure',
};

export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category.replace(/_/g, ' ');
}

export const CATEGORY_NAMES: Record<string, string> = {
  agent: 'Agent',
  pipeline: 'Pipeline',
  automation: 'Automation',
  portal: 'Portal',
  integration: 'Integration',
  platform: 'Platform',
};

export function systemCategoryLabel(category: string): string {
  return CATEGORY_NAMES[category] ?? category;
}

export type Tone = 'good' | 'bad' | 'neutral';

/** The server's "no value recorded yet" signal: a null Net. The page never infers it from benefit_usd === 0. */
export const VALUE_NOT_RECORDED = 'value not recorded yet';

/** Neutral until a value is recorded (null Net); then good / bad by the sign of the Net. */
export function netTone(money: Pick<MoneySummary, 'net_usd'>): Tone {
  if (money.net_usd == null) return 'neutral';
  if (money.net_usd > 0) return 'good';
  if (money.net_usd < 0) return 'bad';
  return 'neutral';
}

/** Signed Net: "+$780", "-$42.00", "$0.00"; "—" when no value is recorded. */
export function formatNet(net: number | null | undefined): string {
  if (net == null) return '—';
  return `${net > 0 ? '+' : ''}${formatUsd(net)}`;
}

/** One line for a card: "Benefit $1.2k · Cost $420 · Net +$780", or
 *  "Cost $420 · value not recorded yet" while no benefit line exists. */
export function moneyLine(money: MoneySummary): string {
  const parts = [`Cost ${formatUsd(money.cost_usd, { compact: true })}`];
  if (money.funding_usd > 0) parts.push(`Funded ${formatUsd(money.funding_usd, { compact: true })}`);
  const net = money.net_usd;
  if (net == null) {
    parts.push(VALUE_NOT_RECORDED);
    return parts.join(' · ');
  }
  parts.unshift(`Benefit ${formatUsd(money.benefit_usd, { compact: true })}`);
  parts.push(`Net ${net > 0 ? '+' : ''}${formatUsd(net, { compact: true })}`);
  return parts.join(' · ');
}

export interface LedgerGroup {
  kind: string;
  label: string;
  entries: LedgerEntry[];
  total_usd: number;
}

/** Group ledger rows by kind in display order; rejected rows stay listed but never sum. */
export function groupLedger(entries: LedgerEntry[]): LedgerGroup[] {
  const order = ['benefit', 'cost', 'funding', 'risk'];
  const groups = new Map<string, LedgerEntry[]>();
  for (const e of entries) {
    const list = groups.get(e.entry_kind) ?? [];
    list.push(e);
    groups.set(e.entry_kind, list);
  }
  const kinds = [...order.filter((k) => groups.has(k)), ...[...groups.keys()].filter((k) => !order.includes(k))];
  return kinds.map((kind) => {
    const list = groups.get(kind) ?? [];
    return {
      kind,
      label: KIND_LABELS[kind] ?? kind,
      entries: list,
      total_usd: list.filter((e) => e.status !== 'rejected').reduce((sum, e) => sum + (e.amount_usd ?? 0), 0),
    };
  });
}

/** Period label: "Sep 1 – Sep 30, 2026", "to Sep 30, 2026", or "undated". */
export function periodLabel(start: string | null, end: string | null): string {
  if (!start && !end) return 'undated';
  if (start && end) {
    const s = parseUtc(start);
    const e = parseUtc(end);
    if (s && e) {
      const sameYear = s.getFullYear() === e.getFullYear();
      const sFmt = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
      const eFmt = e.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `${sFmt} – ${eFmt}`;
    }
  }
  return `to ${formatStamp(end ?? start)}`;
}
